import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const Platform = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { t, language } = useLanguage();

  const getMockups = () => [
    {
      id: 'customer',
      label: 'Customer App',
      title: 'Order water in seconds',
      description: 'A streamlined mobile experience for requesting water delivery, tracking orders in real-time, and managing payment — all from your phone.',
      features: ['Select tanker capacity', 'Automatic station matching', 'Live GPS tracking', 'Digital payment options'],
      mockup: (
        <div className="relative mx-auto w-64 h-[520px] bg-watrly-slate rounded-[3rem] p-3 shadow-2xl">
          <div className="absolute top-6 left-1/2 -translate-x-1/2 w-20 h-6 bg-watrly-slate rounded-full" />
          <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
            <div className="h-full bg-gradient-to-b from-watrly-turquoise/5 to-white p-4">
              <div className="flex items-center justify-between mb-6 pt-8">
                <div>
                  <div className="text-xs text-watrly-slate/50">{t.platform.mockup.welcomeBack}</div>
                  <div className="font-semibold text-watrly-slate">{language === 'ar' ? 'محمد' : 'Mohammed'}</div>
                </div>
                <div className="w-10 h-10 rounded-full bg-watrly-turquoise/20" />
              </div>
              
              <div className="bg-watrly-turquoise rounded-2xl p-4 mb-4 text-white">
                <div className="text-xs opacity-80 mb-1">{t.platform.mockup.activeDelivery}</div>
                <div className="font-semibold mb-2">32 {t.platform.mockup.tonTanker}</div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  <span className="text-sm">{t.platform.mockup.enRoute} - 12 {t.platform.mockup.minAway}</span>
                </div>
              </div>

              <div className="mb-4">
                <div className="text-sm font-medium text-watrly-slate mb-3">{t.platform.mockup.selectCapacity}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 rounded-xl border-2 border-watrly-slate/10 text-center">
                    <div className="text-lg font-bold text-watrly-slate">18</div>
                    <div className="text-xs text-watrly-slate/50">{t.platform.mockup.ton}</div>
                  </div>
                  <div className="p-3 rounded-xl border-2 border-watrly-turquoise bg-watrly-turquoise/5 text-center">
                    <div className="text-lg font-bold text-watrly-turquoise">32</div>
                    <div className="text-xs text-watrly-turquoise/70">{t.platform.mockup.ton}</div>
                  </div>
                </div>
              </div>

              <div className="bg-watrly-sand-light rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-watrly-turquoise/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-watrly-turquoise" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-watrly-slate/50">{t.platform.mockup.deliveryLocation}</div>
                    <div className="text-sm font-medium text-watrly-slate">{language === 'ar' ? 'الملقا، الرياض' : 'Al Malqa, Riyadh'}</div>
                  </div>
                </div>
                <div className="h-20 bg-watrly-turquoise/10 rounded-xl flex items-center justify-center">
                  <span className="text-xs text-watrly-turquoise/50">{t.platform.mockup.mapPreview}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'admin',
      label: 'Admin Dashboard',
      title: 'Complete operational control',
      description: 'A powerful control center for station owners and administrators to manage orders, monitor drivers, and track fleet performance.',
      features: ['Order management', 'Driver assignment', 'Fleet tracking', 'Performance analytics'],
      mockup: (
        <div className="relative mx-auto w-full max-w-lg bg-watrly-slate rounded-2xl p-2 shadow-2xl">
          <div className="flex items-center gap-2 px-4 py-3">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 text-center">
              <span className="text-xs text-white/50">Watrly Admin</span>
            </div>
          </div>
          <div className="bg-white rounded-xl overflow-hidden">
            <div className="p-4">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-lg font-bold text-watrly-slate">{t.platform.mockup.operationsDashboard}</div>
                  <div className="text-sm text-watrly-slate/50">{t.platform.mockup.realtimeOverview}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-sm text-green-600">{t.platform.mockup.live}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="p-3 rounded-xl bg-watrly-turquoise/10">
                  <div className="text-2xl font-bold text-watrly-turquoise">24</div>
                  <div className="text-xs text-watrly-slate/50">{t.platform.mockup.activeOrders}</div>
                </div>
                <div className="p-3 rounded-xl bg-watrly-sand-light">
                  <div className="text-2xl font-bold text-watrly-slate">12</div>
                  <div className="text-xs text-watrly-slate/50">{t.platform.mockup.driversOnline}</div>
                </div>
                <div className="p-3 rounded-xl bg-green-50">
                  <div className="text-2xl font-bold text-green-600">98%</div>
                  <div className="text-xs text-watrly-slate/50">{t.platform.mockup.onTimeRate}</div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium text-watrly-slate mb-2">{t.platform.mockup.recentOrders}</div>
                {[
                  { id: '#4521', status: t.platform.mockup.delivering, statusKey: 'Delivering', driver: language === 'ar' ? '.أحمد ك' : 'Ahmed K.', time: `12 ${t.platform.mockup.min}` },
                  { id: '#4520', status: t.platform.mockup.filling, statusKey: 'Filling', driver: language === 'ar' ? '.عمر س' : 'Omar S.', time: `25 ${t.platform.mockup.min}` },
                  { id: '#4519', status: t.platform.mockup.assigned, statusKey: 'Assigned', driver: language === 'ar' ? '.خالد م' : 'Khalid M.', time: `3 ${t.platform.mockup.min}` },
                ].map((order, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-watrly-sand-light/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-watrly-turquoise/20 flex items-center justify-center text-xs font-bold text-watrly-turquoise">
                        {order.driver.split(' ')[0][0]}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-watrly-slate">{order.id}</div>
                        <div className="text-xs text-watrly-slate/50">{order.driver}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs font-medium ${
                        order.statusKey === 'Delivering' ? 'text-watrly-turquoise' :
                        order.statusKey === 'Filling' ? 'text-amber-500' : 'text-green-600'
                      }`}>{order.status}</div>
                      <div className="text-xs text-watrly-slate/40">{order.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'tracking',
      label: 'Live Tracking',
      title: 'Visibility at every step',
      description: 'Real-time status updates from order placement through delivery completion. Know exactly where your water is at all times.',
      features: ['GPS location tracking', 'Status notifications', 'ETA updates', 'Delivery confirmation'],
      mockup: (
        <div className="relative mx-auto w-64 h-[520px] bg-watrly-slate rounded-[3rem] p-3 shadow-2xl">
          <div className="absolute top-6 left-1/2 -translate-x-1/2 w-20 h-6 bg-watrly-slate rounded-full" />
          <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
            <div className="h-full">
              <div className="h-1/2 bg-watrly-turquoise/10 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full border-2 border-dashed border-watrly-turquoise/30 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-full border-2 border-dashed border-watrly-turquoise/50 flex items-center justify-center">
                        <div className="w-4 h-4 rounded-full bg-watrly-turquoise animate-ping absolute opacity-50" />
                        <div className="w-4 h-4 rounded-full bg-watrly-turquoise" />
                      </div>
                    </div>
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2">
                      <div className="bg-watrly-turquoise text-white text-xs px-2 py-1 rounded-full font-medium">
                        12 min
                      </div>
                    </div>
                    <svg className="absolute bottom-2 right-2 w-8 h-8 text-watrly-turquoise" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3.05a2.5 2.5 0 014.9 0H19a1 1 0 001-1V5a1 1 0 00-1-1H3z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="h-1/2 p-4 pt-6">
                <div className="text-center mb-4">
                  <div className="text-xs text-watrly-slate/50 mb-1">{t.platform.mockup.deliveryStatus}</div>
                  <div className="text-lg font-bold text-watrly-turquoise">{t.platform.mockup.onTheWay}</div>
                </div>
                
                <div className="space-y-3">
                  {[
                    { label: t.platform.mockup.orderPlaced, done: true },
                    { label: t.platform.mockup.driverAssigned, done: true },
                    { label: t.platform.mockup.fillingComplete, done: true },
                    { label: t.platform.mockup.enRoute, active: true },
                    { label: language === 'ar' ? 'تم التوصيل' : 'Delivered', done: false },
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        step.done ? 'bg-watrly-turquoise' :
                        step.active ? 'bg-watrly-turquoise animate-pulse' : 'bg-watrly-slate/10'
                      }`}>
                        {step.done && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {step.active && <div className="w-2 h-2 bg-white rounded-full" />}
                      </div>
                      <span className={`text-sm ${step.done || step.active ? 'text-watrly-slate font-medium' : 'text-watrly-slate/40'}`}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section id="platform" className="section-padding bg-gradient-to-b from-watrly-sand-light/50 via-watrly-sand-warm/30 to-watrly-cream relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(10,186,181,0.08),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(201,184,150,0.12),transparent_50%)]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-watrly-desert/15 to-transparent" />

      <div className="container-watrly relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-6 md:mb-10 lg:mb-12"
        >
          <span className="inline-block text-xs md:text-sm font-semibold text-watrly-turquoise uppercase tracking-widest mb-2 md:mb-4">
            {t.platform.badge}
          </span>
          <h2 className="heading-section mb-3 md:mb-6">
            {t.platform.title}
          </h2>
          <p className="text-body-lg">
            {t.platform.subtitle}
          </p>
        </motion.div>

        <div className="flex justify-center mb-6 md:mb-10">
          <div className="inline-flex p-1 md:p-1.5 bg-white rounded-full shadow-lg shadow-watrly-slate/5">
            {[t.platform.tabs.customer, t.platform.tabs.admin, t.platform.tabs.tracking].map((label, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`px-3 py-2 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300 ${
                  activeTab === index
                    ? 'bg-watrly-turquoise text-white shadow-lg'
                    : 'text-watrly-slate/60 hover:text-watrly-slate'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid lg:grid-cols-2 gap-6 md:gap-10 lg:gap-12 items-center"
          >
            <div className="order-2 lg:order-1">
              <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-watrly-slate mb-2 md:mb-4">
                {activeTab === 0 ? t.platform.customer.title : activeTab === 1 ? t.platform.admin.title : t.platform.tracking.title}
              </h3>
              <p className="text-sm md:text-base text-watrly-slate/60 leading-relaxed mb-4 md:mb-6">
                {activeTab === 0 ? t.platform.customer.description : activeTab === 1 ? t.platform.admin.description : t.platform.tracking.description}
              </p>
              
              <div className="space-y-2 md:space-y-3">
                {(activeTab === 0 ? t.platform.customer.features : activeTab === 1 ? t.platform.admin.features : t.platform.tracking.features).map((feature, index) => (
                  <motion.div
                    key={feature}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-watrly-turquoise/10 flex items-center justify-center">
                      <svg className="w-3 h-3 text-watrly-turquoise" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-sm md:text-base text-watrly-slate font-medium">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="order-1 lg:order-2 flex justify-center">
              {getMockups()[activeTab].mockup}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Platform;
